import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<PatientProjectionDetailStep />` — single-record detail view with
 * the full rendered narrative, provenance metadata, and a back-to-list
 * action.
 *
 * Provenance fields shown: source audit record ID, anchored
 * timestamp, content category, taxonomy version (for support /
 * debugging — Issue #41 editorial-artifact ownership). Patients can
 * verify a rendering against substrate audit-chain provenance using
 * the source audit record ID.
 */
import * as React from 'react';
import { useLocale } from '../../i18n/locale-context.js';
export function PatientProjectionDetailStep(props) {
    const { t } = useLocale();
    const [state, setState] = React.useState({
        rendered: null,
        version: null,
        renderError: null,
    });
    React.useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const version = await props.taxonomy.currentVersion();
                if (props.content.kind === 'audit-chain') {
                    const rendered = await props.taxonomy.render({
                        record: props.content.auditRecord,
                        locale: props.locale,
                    });
                    if (!cancelled) {
                        setState({ rendered, version, renderError: null });
                    }
                }
                else if (!cancelled) {
                    setState({ rendered: null, version, renderError: null });
                }
            }
            catch (e) {
                if (!cancelled) {
                    setState({
                        rendered: null,
                        version: null,
                        renderError: e instanceof Error ? e.message : String(e),
                    });
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [props.content, props.taxonomy, props.locale]);
    const record = props.decrypted.record;
    return (_jsxs("section", { "aria-labelledby": "projection-detail-heading", children: [_jsx("button", { type: "button", onClick: props.onBack, children: t('projection.detail.back_button') }), _jsx("h3", { id: "projection-detail-heading", children: t('projection.detail.heading') }), state.renderError !== null ? (_jsx("p", { role: "alert", children: t('projection.detail.render_failed', {
                    detail: state.renderError,
                }) })) : props.content.kind === 'audit-chain' ? (_jsx("p", { children: state.rendered?.narrative ?? t('projection.detail.rendering') })) : (_jsx("p", { children: t('projection.list.aggregated_payload_summary', {
                    operationId: props.content.sourceOperationId,
                    receivedAt: props.content.aggregatedAtSubstrate,
                }) })), _jsxs("dl", { children: [_jsx("dt", { children: t('projection.detail.field.anchored_at') }), _jsx("dd", { children: record.projectionAnchoredAt }), _jsx("dt", { children: t('projection.detail.field.source_audit_anchored_at') }), _jsx("dd", { children: record.sourceAuditAnchoredAt }), _jsx("dt", { children: t('projection.detail.field.source_audit_record_id') }), _jsx("dd", { children: _jsx("code", { children: record.sourceAuditRecordId }) }), _jsx("dt", { children: t('projection.detail.field.content_category') }), _jsx("dd", { children: record.contentCategory }), _jsx("dt", { children: t('projection.detail.field.locale') }), _jsx("dd", { children: state.rendered?.locale ?? props.locale }), state.version !== null ? (_jsxs(_Fragment, { children: [_jsx("dt", { children: t('projection.detail.field.taxonomy_version') }), _jsx("dd", { children: _jsx("code", { children: state.version.versionId }) })] })) : null] })] }));
}
//# sourceMappingURL=detail-step.js.map